/**
 * Browser detection.
 * @module browser
 * @license MIT
 *
 * @copyright 2010 Jeff Schiller, 2010 Alexis Deveria
 */

import 'pathseg';

import { NS } from './namespaces';

const supportsSVG_ = (function () {
    return Boolean(document.createElementNS && document.createElementNS(NS.SVG, 'svg').createSVGRect);
}());

/**
 * @function module:browser.supportsSvg
 * @returns {boolean}
*/
export const supportsSvg = () => supportsSVG_;

const { userAgent } = navigator;
const svg = document.createElementNS(NS.SVG, 'svg');

// Note: Browser sniffing should only be used if no other detection method is possible
const isOpera_ = Boolean((window as any).opera);
const isWebkit_ = userAgent.includes('AppleWebKit');
const isGecko_ = userAgent.includes('Gecko/');
const isChrome_ = userAgent.includes('Chrome/');
const isEdge_ = userAgent.includes('Edg/');
const isSafari_ = userAgent.includes('Safari/') && !isChrome_ && !isEdge_;
const isWindows_ = userAgent.includes('Windows');
const isMac_ = userAgent.includes('Macintosh');
const isTouch_ = 'ontouchstart' in window;

const supportsSelectors_ = (function () {
    return Boolean(svg.querySelector);
}());

const supportsXpath_ = (function () {
    return Boolean(document.evaluate);
}());

// segList functions (for FF1.5 and 2.0)
const supportsPathReplaceItem_ = (function () {
    const path = document.createElementNS(NS.SVG, 'path');
    path.setAttribute('d', 'M0,0 10,10');
    const seglist = path.pathSegList;
    const seg = path.createSVGPathSegLinetoAbs(5, 5);
    try {
        seglist.replaceItem(seg, 1);
        return true;
    } catch (err) {/* empty */ }
    return false;
}());

const supportsPathInsertItemBefore_ = (function () {
    const path = document.createElementNS(NS.SVG, 'path');
    path.setAttribute('d', 'M0,0 10,10');
    const seglist = path.pathSegList;
    const seg = path.createSVGPathSegLinetoAbs(5, 5);
    try {
        seglist.insertItemBefore(seg, 1);
        return true;
    } catch (err) {/* empty */ }
    return false;
}());

// text character positioning (for IE9 and now Chrome)
const supportsGoodTextCharPos_ = (function () {
    const svgroot = document.createElementNS(NS.SVG, 'svg');
    const svgcontent = document.createElementNS(NS.SVG, 'svg');

    document.documentElement.append(svgroot);
    svgcontent.setAttribute('x', "5");
    svgroot.append(svgcontent);

    const text = document.createElementNS(NS.SVG, 'text');
    text.textContent = 'a';
    svgcontent.append(text);

    let pos = 0;
    try { // Chrome now fails here
        pos = text.getStartPositionOfChar(0).x;
    } catch (err) {
        return false;
    } finally {
        svgroot.remove();
    }
    return pos === 0;
}());

const supportsPathBBox_ = (function () {
    const svgcontent = document.createElementNS(NS.SVG, 'svg');
    document.documentElement.append(svgcontent);
    const path = document.createElementNS(NS.SVG, 'path');
    path.setAttribute('d', 'M0,0 C0,0 10,10 10,0');
    svgcontent.append(path);
    const bbox = path.getBBox();
    svgcontent.remove();
    return (bbox.height > 4 && bbox.height < 5);
}());

// Support for correct bbox sizing on groups with horizontal/vertical lines
const supportsHVLineContainerBBox_ = (function () {
    const svgcontent = document.createElementNS(NS.SVG, 'svg');
    document.documentElement.append(svgcontent);
    const path = document.createElementNS(NS.SVG, 'path');
    path.setAttribute('d', 'M0,0 10,0');
    const path2 = document.createElementNS(NS.SVG, 'path');
    path2.setAttribute('d', 'M5,0 15,0');
    const g = document.createElementNS(NS.SVG, 'g');
    g.append(path, path2);
    svgcontent.append(g);
    const bbox = g.getBBox();
    svgcontent.remove();
    // Webkit gives 0, FF gives 10, Opera (correctly) gives 15
    return (bbox.width === 15);
}());

const supportsEditableText_ = (function () {
    // TODO: Find better way to check support for this
    return isOpera_;
}());

const supportsNonScalingStroke_ = (function () {
    const rect = document.createElementNS(NS.SVG, 'rect');
    rect.setAttribute('style', 'vector-effect:non-scaling-stroke');
    return (rect.style as any).vectorEffect === 'non-scaling-stroke';
}());

// Public API

/**
 * @function module:browser.isOpera
 * @returns {boolean}
*/
export const isOpera = () => isOpera_;
/**
 * @function module:browser.isWebkit
 * @returns {boolean}
*/
export const isWebkit = () => isWebkit_;
/**
 * @function module:browser.isGecko
 * @returns {boolean}
*/
export const isGecko = () => isGecko_;
/**
 * @function module:browser.isChrome
 * @returns {boolean}
*/
export const isChrome = () => isChrome_;
/**
 * @function module:browser.isSafari
 * @returns {boolean}
*/
export const isSafari = () => isSafari_;
/**
 * @function module:browser.isEdge
 * @returns {boolean}
*/
export const isEdge = () => isEdge_;
/**
 * @function module:browser.isWindows
 * @returns {boolean}
*/
export const isWindows = () => isWindows_;
/**
 * @function module:browser.isMac
 * @returns {boolean}
*/
export const isMac = () => isMac_;
/**
 * @function module:browser.isTouch
 * @returns {boolean}
*/
export const isTouch = () => isTouch_;

/**
 * @function module:browser.supportsSelectors
 * @returns {boolean}
*/
export const supportsSelectors = () => supportsSelectors_;

/**
 * @function module:browser.supportsXpath
 * @returns {boolean}
*/
export const supportsXpath = () => supportsXpath_;

/**
 * @function module:browser.supportsPathReplaceItem
 * @returns {boolean}
*/
export const supportsPathReplaceItem = () => supportsPathReplaceItem_;

/**
 * @function module:browser.supportsPathInsertItemBefore
 * @returns {boolean}
*/
export const supportsPathInsertItemBefore = () => supportsPathInsertItemBefore_;

/**
 * @function module:browser.supportsPathBBox
 * @returns {boolean}
*/
export const supportsPathBBox = () => supportsPathBBox_;

/**
 * @function module:browser.supportsHVLineContainerBBox
 * @returns {boolean}
*/
export const supportsHVLineContainerBBox = () => supportsHVLineContainerBBox_;

/**
 * @function module:browser.supportsGoodTextCharPos
 * @returns {boolean}
*/
export const supportsGoodTextCharPos = () => supportsGoodTextCharPos_;

/**
* @function module:browser.supportsEditableText
 * @returns {boolean}
*/
export const supportsEditableText = () => supportsEditableText_;

/**
* @function module:browser.supportsNonScalingStroke
* @returns {boolean}
*/
export const supportsNonScalingStroke = () => supportsNonScalingStroke_;
